import { CalendarDays, GripVertical } from "lucide-react";
import {
  defaultCollisionDetection,
  type CollisionDetector,
} from "@dnd-kit/collision";
import { useSortable } from "@dnd-kit/react/sortable";

import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import type { BoardCard, BoardMember, CardPriority } from "@/features/board/types";
import { cn } from "@/lib/utils";

type TaskCardProps = {
  card: BoardCard;
  columnId: string;
  index: number;
  members: BoardMember[];
  onOpen: (card: BoardCard) => void;
};

const priorityStyles: Record<CardPriority, string> = {
  low: "bg-neutral-100 text-neutral-600",
  medium: "bg-blue-50 text-blue-700",
  high: "bg-amber-100 text-amber-900",
  urgent: "bg-red-100 text-red-800",
};

const priorityLabels: Record<CardPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

const dueLabel = (value: string) =>
  new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(
    new Date(value)
  );

const ignoreActiveCardCollision: CollisionDetector = (input) =>
  input.dragOperation.source?.id === input.droppable.id
    ? null
    : defaultCollisionDetection(input);

export const TaskCard = ({ card, columnId, index, members, onOpen }: TaskCardProps) => {
  const { ref, handleRef, isDragging } = useSortable({
    id: card.id,
    index,
    group: columnId,
    type: "card",
    accept: "card",
    collisionDetector: ignoreActiveCardCollision,
    data: card,
  });
  const assignee = members.find((member) => member.id === card.assigneeId);
  const overdue = card.dueAt ? new Date(card.dueAt).getTime() < Date.now() : false;

  return (
    <article
      className={cn(
        "rounded-lg bg-white p-3 shadow-sm ring-1 ring-neutral-950/5",
        isDragging && "opacity-40"
      )}
      data-card-id={card.id}
      ref={ref}
    >
      <div className="flex items-start justify-between gap-2">
        <Badge className={priorityStyles[card.priority]} variant="secondary">
          {priorityLabels[card.priority]}
        </Badge>
        <button
          aria-label={`Drag ${card.title}`}
          className="relative -translate-y-1 translate-x-1 cursor-grab rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:cursor-grabbing before:absolute before:top-1/2 before:left-1/2 before:size-[max(100%,3rem)] before:-translate-1/2 before:content-[''] pointer-fine:before:hidden"
          onClick={(event) => event.stopPropagation()}
          ref={handleRef}
          type="button"
        >
          <GripVertical className="size-4 shrink-0 stroke-current" />
        </button>
      </div>
      <button
        aria-label={`Open ${card.title}`}
        className="w-full rounded-md pt-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        onClick={() => onOpen(card)}
        type="button"
      >
        <h3 className="text-balance text-base font-medium text-neutral-950 sm:text-sm">
          {card.title}
        </h3>
        {card.description ? (
          <p className="text-pretty line-clamp-2 pt-1.5 text-base text-neutral-500 sm:text-sm">
            {card.description}
          </p>
        ) : null}
        {card.dueAt || assignee ? (
          <span className="flex items-center justify-between gap-3 pt-4">
            {card.dueAt ? (
              <span
                className={cn(
                  "flex items-center gap-1.5 text-base tabular-nums sm:text-sm",
                  overdue ? "text-red-700" : "text-neutral-500"
                )}
              >
                <CalendarDays className="size-4 shrink-0 stroke-current" />
                {dueLabel(card.dueAt)}
              </span>
            ) : (
              <span />
            )}
            {assignee ? (
              <UserAvatar className="size-6 text-[0.625rem]" image={assignee.image} name={assignee.name} />
            ) : null}
          </span>
        ) : null}
      </button>
    </article>
  );
};
